import React, { useState } from 'react';
import { Form, Button, Container, Row, Col, Card, Modal, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './Login.css';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Password reset states
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const loggedInUser = await response.json();
        
        // Store JWT token if present in response
        if (loggedInUser.jwtToken) {
          localStorage.setItem('jwtToken', loggedInUser.jwtToken);
          localStorage.setItem('tokenType', loggedInUser.tokenType || 'Bearer');
          localStorage.setItem('userId', loggedInUser.id);
          localStorage.setItem('role', loggedInUser.role);
          localStorage.setItem('username', loggedInUser.username);
          // store subscription info
          localStorage.setItem('activeSubscription', loggedInUser.activeSubscription ? 'true' : 'false');
          if (loggedInUser.stripeCustomerId) {
            localStorage.setItem('stripeCustomerId', loggedInUser.stripeCustomerId);
          }
          if (loggedInUser.expiryDate) {
            localStorage.setItem('expiryDate', loggedInUser.expiryDate);
          }
          
          // Optional: Set token expiration
          const expirationTime = new Date().getTime() + 60 * 60 * 1000; // 1 hour
          localStorage.setItem('tokenExpiration', expirationTime);
          
          console.log('JWT Token stored successfully');
        }
        
        // Clean up password from user object before storing
        const userWithoutPassword = { ...loggedInUser };
        delete userWithoutPassword.password;
        delete userWithoutPassword.jwtToken; // Don't pass token to parent component
        
        onLogin(userWithoutPassword);
      } else if (response.status === 401) {
        setError('Invalid username or password.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Something went wrong while connecting. Please try again in a few moments.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      if (response.ok) {
        setResetMessage('Password reset link has been sent to your email address.');
        setResetEmail('');
        // Optionally close modal after a delay
        setTimeout(() => {
          setShowPasswordReset(false);
          setResetMessage('');
        }, 3000);
      } else if (response.status === 404) {
        setResetError('No account found with this email address.');
      } else {
        const errorData = await response.json();
        setResetError(errorData.message || 'An error occurred while sending reset email.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setResetError('Something went wrong while connecting. Please try again in a few moments.');
    } finally {
      setResetLoading(false);
    }    
  };

  const handleResetModalClose = () => {
    setShowPasswordReset(false);
    setResetEmail('');
    setResetMessage('');
    setResetError('');
  };

  return (
    <>
      <Container fluid className="login-container">
        <Row className="w-100 h-100">
          <Col className="d-flex justify-content-center align-items-center">
            <Card className="login-card">
              <Card.Body className="text-center">
                <img src="images/logo.png" alt="SkedGuy" className="mb-3" style={{ width: '100%' }}/>
                <p>Please login to your account</p>
                {error && <p className="text-danger">{error}</p>}
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="formUsername">
                    <Form.Control
                      type="text"
                      placeholder="Email Address"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formPassword">
                    <Form.Control
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Button
                    className="login-button w-100"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Logging in...' : 'Log In'}
                  </Button>
                </Form>
                
                {/* Password Reset Link */}
                <Button
                  variant="link"
                  className="mt-2 p-0"
                  onClick={() => setShowPasswordReset(true)}
                  style={{ textDecoration: 'none', fontSize: '0.9rem' }}
                >
                  Forgot Password?
                </Button>
                
                <Button
                  variant="link"
                  className="mt-2"
                  onClick={() => navigate('/register')}
                >
                  Don't have an account? Register
                </Button>
                
                <div className="mt-2">
                  <a 
                    href="https://skedguy.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-link p-0"
                    style={{ textDecoration: 'none', fontSize: '0.9rem' }}
                  >
                    Want more information? Check out our home page
                  </a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Password Reset Modal */}
      <Modal show={showPasswordReset} onHide={handleResetModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetMessage && <Alert variant="success">{resetMessage}</Alert>}
          {resetError && <Alert variant="danger">{resetError}</Alert>}
          
          <p className="mb-3">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          
          <Form onSubmit={handlePasswordReset}>
            <Form.Group className="mb-3" controlId="resetEmail">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </Form.Group>
            
            <div className="d-flex gap-2">
              <Button
                variant="secondary"
                onClick={handleResetModalClose}
                disabled={resetLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={resetLoading || !resetEmail}
                className="flex-fill"
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Login;